import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PromptStep from './PromptStep';
import PhoneVerifyStep from './PhoneVerifyStep';
import { useAuth } from '../context/AuthContext';

// UPDATED 2026-07-22: sso-add-phone's SMS stage is fully unbound —
// Authentik's SMS Authenticator Setup Stage has a confirmed internal bug
// (provider-agnostic "Invalid phone number" validation failure). This
// flow now has ONLY the order-10 prompt stage, so submitting it always
// returns { done: true } immediately from the backend. We intercept that
// and show our own PhoneVerifyStep instead of navigating away right away
// — only once that succeeds do we actually treat this as complete. See
// phoneVerification.js (backend) header for the full investigation.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

export default function AddPhone() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(null);

  const username = user?.profile?.preferred_username;

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check
  // possible. Every request below already has its own 401 handling that
  // redirects to '/' — that now does the whole job on its own.
  useEffect(() => {
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchChallenge() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-phone/challenge`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  async function submitStep(body) {
    const submittedPhone = body.phone;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-phone/step`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();

      // The flow only has the prompt stage now — success here means "the
      // prompt was accepted," not "the phone is verified." Intercept and
      // show our own verification instead of treating this as complete.
      if (data.done) {
        if (!submittedPhone) {
          // Shouldn't happen (prompt requires a phone), but fail safe.
          navigate('/dashboard', { state: { phoneAdded: true } });
          return;
        }
        setVerifiedPhone(submittedPhone);
        setShowPhoneVerify(true);
        return;
      }

      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Something went wrong. Please try again.');
      }

      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  function handlePhoneVerified() {
    navigate('/dashboard', { state: { phoneAdded: true } });
  }

  function handleCancel() {
    navigate('/dashboard');
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            Add Phone Number
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            {showPhoneVerify ? 'Verify your phone number' : (challenge?.title || 'Loading…')}
          </p>
        </div>

        {user && !showPhoneVerify && (
          <div style={{
            background: '#F0F4F8', borderRadius: '8px', padding: '10px 14px',
            marginBottom: '20px', textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Adding a number for
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
              {user.profile?.name || user.profile?.preferred_username}
            </p>
            {user.profile?.email && (
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>{user.profile.email}</p>
            )}
          </div>
        )}

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {loading && !challenge && !showPhoneVerify && (
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
        )}

        {showPhoneVerify ? (
          <PhoneVerifyStep
            phone={verifiedPhone}
            username={username}
            onVerified={handlePhoneVerified}
            onCancel={handleCancel}
          />
        ) : (
          <>
            {challenge?.stage === 'ak-stage-prompt' && (
              <>
                <PromptStep
                  fields={challenge.raw.fields}
                  values={values}
                  setValues={setValues}
                  loading={loading}
                  onSubmit={() => submitStep(values)}
                />
                <button
                  onClick={handleCancel}
                  style={{
                    width: '100%', marginTop: '10px', padding: '12px',
                    background: 'none', border: '1px solid #D3D1C7', color: '#666',
                    borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </>
            )}

            {challenge && challenge.stage !== 'ak-stage-prompt' && (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  Something went wrong adding your phone number. Please try again later or contact support.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    background: 'none', border: '1px solid #009FE3', color: '#009FE3',
                    padding: '10px 20px', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  Back to Dashboard
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
